import type { TimelineEnvelope } from '../../types';

/**
 * Timeline sort key — envelopes carry `effective_date` (server side =
 * COALESCE(authored_at, created_at)). We accept partial shapes so the
 * helper works for any chronological item.
 */
export function envelopeSortDate(e: TimelineEnvelope): string {
  return e.effective_date || '';
}

/**
 * Generated documents (lab orders, form PDFs, markdown note exports) flow
 * through the same `kind=document` envelope as DocRefs but originate from
 * the legacy DocumentMetadata table. They have no enrichment and use a
 * different download path. Detect via `extracted_by` (the envelope's
 * `derived_via` provenance string).
 */
export function isGeneratedDoc(envelope: TimelineEnvelope): boolean {
  return envelope.extracted_by === 'generated_document';
}

export interface PeriodGroup<T> {
  key: string;
  label: string;
  items: T[];
}

/**
 * Bucket items into "This month" / "Last month" / year buckets, using a
 * caller-supplied `getDate` extractor so the same helper works for both
 * artifacts and batch envelopes.
 */
export function groupByPeriod<T>(
  items: T[],
  getDate: (item: T) => string,
  now: Date = new Date(),
): PeriodGroup<T>[] {
  const thisMonth: PeriodGroup<T> = {
    key: 'this-month',
    label: 'This month',
    items: [],
  };
  const lastMonth: PeriodGroup<T> = {
    key: 'last-month',
    label: 'Last month',
    items: [],
  };
  const yearBuckets = new Map<number, PeriodGroup<T>>();

  const lastMoDate = new Date(now);
  lastMoDate.setMonth(now.getMonth() - 1);

  for (const item of items) {
    const iso = getDate(item);
    if (!iso) {
      const bucket =
        yearBuckets.get(0) ?? { key: 'undated', label: 'Undated', items: [] };
      bucket.items.push(item);
      yearBuckets.set(0, bucket);
      continue;
    }
    const d = new Date(iso);
    const sameMonth =
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const sameLastMonth =
      d.getFullYear() === lastMoDate.getFullYear() &&
      d.getMonth() === lastMoDate.getMonth();

    if (sameMonth) {
      thisMonth.items.push(item);
    } else if (sameLastMonth) {
      lastMonth.items.push(item);
    } else {
      const y = d.getFullYear();
      const bucket =
        yearBuckets.get(y) ?? { key: String(y), label: String(y), items: [] };
      bucket.items.push(item);
      yearBuckets.set(y, bucket);
    }
  }

  const byDesc = (a: T, b: T) =>
    new Date(getDate(b) || 0).getTime() - new Date(getDate(a) || 0).getTime();

  thisMonth.items.sort(byDesc);
  lastMonth.items.sort(byDesc);

  const out: PeriodGroup<T>[] = [];
  if (thisMonth.items.length) out.push(thisMonth);
  if (lastMonth.items.length) out.push(lastMonth);

  // Years descending; "undated" (key=0) goes last
  const years = [...yearBuckets.keys()].sort((a, b) => {
    if (a === 0) return 1;
    if (b === 0) return -1;
    return b - a;
  });
  for (const y of years) {
    const bucket = yearBuckets.get(y);
    if (!bucket) continue;
    bucket.items.sort(byDesc);
    out.push(bucket);
  }
  return out;
}

/** "Mar 12, 2026" / "Mar 12 → Apr 8, 2026" / "Mar 12, 2024 → Apr 8, 2026" */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const fullOpts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  if (!e || start === end) return s.toLocaleDateString(undefined, fullOpts);
  if (s.getFullYear() === e.getFullYear()) {
    const shortOpts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };
    return `${s.toLocaleDateString(undefined, shortOpts)} → ${e.toLocaleDateString(undefined, fullOpts)}`;
  }
  return `${s.toLocaleDateString(undefined, fullOpts)} → ${e.toLocaleDateString(undefined, fullOpts)}`;
}

/**
 * Card copy for in-progress DocRefs. The terminal states (`enriched` /
 * `failed`) render a populated card or error — `pending` shows a skeleton
 * with this caption.
 */
export function docRefStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'pending':
      return 'Reading document…';
    case 'failed':
      return 'Processing failed';
    default:
      return '';
  }
}

export const TERMINAL_DOCREF_STATUSES = new Set(['enriched', 'failed']);

/** Renders the pre-DocRef "we have an artifact but no clinical record yet"
 *  ghost cards keyed off a known source_artifact_id. */
export function uploadStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'uploaded':
      return 'Uploaded — extracting records…';
    case 'decomposed':
      return 'Records identified — enriching…';
    case 'parsing':
      return 'Parsing pages…';
    case 'parsed':
      return 'Pages parsed — extracting fields…';
    case 'failed':
      return 'Upload processing failed';
    default:
      return 'Processing…';
  }
}
